export enum AccountStatus {
  Active = 'ACTIVE',
  Provisioned = 'PROVISIONED'
}

export enum ActionType {
  Alert = 'ALERT',
  Reminder = 'REMINDER'
}

export enum ActivityInboxType {
  IssueMessageCreated = 'ISSUE_MESSAGE_CREATED',
  IssueUpdated = 'ISSUE_UPDATED'
}

export enum ActivityOption {
  Mentioned = 'MENTIONED',
  NotScheduled = 'NOT_SCHEDULED',
  Pinned = 'PINNED',
  Scheduled = 'SCHEDULED'
}

export enum ActivityType {
  AssetCreated = 'ASSET_CREATED',
  BlueCreated = 'BLUE_CREATED',
  CampaignCreated = 'CAMPAIGN_CREATED',
  FeatureToggleUpdated = 'FEATURE_TOGGLE_UPDATED',
  GreenCreated = 'GREEN_CREATED',
  GroupCreated = 'GROUP_CREATED',
  IssueDeleted = 'ISSUE_DELETED',
  IssueUpdated = 'ISSUE_UPDATED',
  MediaOpened = 'MEDIA_OPENED',
  MessageCreated = 'MESSAGE_CREATED',
  PurpleCreated = 'PURPLE_CREATED',
  RedCreated = 'RED_CREATED',
  TagScanCreated = 'TAG_SCAN_CREATED',
  UserJoined = 'USER_JOINED',
  UserJoinedWorkspace = 'USER_JOINED_WORKSPACE',
  UserLeftWorkspace = 'USER_LEFT_WORKSPACE',
  YellowCreated = 'YELLOW_CREATED'
}

export enum AlertStatus {
  Disabled = 'DISABLED',
  Enabled = 'ENABLED'
}

export enum CallState {
  Active = 'ACTIVE',
  Cancelled = 'CANCELLED',
  Declined = 'DECLINED',
  Ended = 'ENDED',
  Ringing = 'RINGING'
}

export enum CampaignStatus {
  Draft = 'DRAFT',
  Scheduled = 'SCHEDULED',
  Sent = 'SENT'
}

export enum FeatureName {
  AccountPage = 'ACCOUNT_PAGE',
  ActivitySortFilter = 'ACTIVITY_SORT_FILTER',
  Alerts = 'ALERTS',
  AssetGrouping = 'ASSET_GROUPING',
  AssetSortFilter = 'ASSET_SORT_FILTER',
  BulkInvites = 'BULK_INVITES',
  CampaignsPage = 'CAMPAIGNS_PAGE',
  CommunicationPlatform = 'COMMUNICATION_PLATFORM',
  CropCircleRedesign = 'CROP_CIRCLE_REDESIGN',
  FilesPage = 'FILES_PAGE',
  GroupsPage = 'GROUPS_PAGE',
  IssueMessageReaction = 'ISSUE_MESSAGE_REACTION',
  Mentions = 'MENTIONS',
  MobileAlertsFlow = 'MOBILE_ALERTS_FLOW',
  NewRegistrationFlow = 'NEW_REGISTRATION_FLOW',
  NewRoutinesPage = 'NEW_ROUTINES_PAGE',
  OemHomeDashboard = 'OEM_HOME_DASHBOARD',
  OrangeButton = 'ORANGE_BUTTON',
  PeopleGrouping = 'PEOPLE_GROUPING',
  PulseHomeDashboard = 'PULSE_HOME_DASHBOARD',
  ReassignIssue = 'REASSIGN_ISSUE',
  Routines = 'ROUTINES',
  SmsLogin = 'SMS_LOGIN'
}

export enum GeoJsonType {
  Point = 'Point'
}

export enum GrippLinkType {
  Asset = 'ASSET',
  Workspace = 'WORKSPACE'
}

/**
 * Entity type for the map plotting-mode toggle (Phase 2). Selecting a card plots
 * that entity's individual points; the default (no mode) plots aggregated
 * Locations. ASSET/ACTIVITY/ROUTINE are plottable, as is PRODUCT (one point per
 * located asset, tagged with the asset's product name). Campaigns/Customers are
 * not plottable.
 */
export enum HomeEntityType {
  Activity = 'ACTIVITY',
  Asset = 'ASSET',
  Product = 'PRODUCT',
  Routine = 'ROUTINE'
}

/**
 * Outcome of an `ingestInboundEmail` call. The processor completes the queue
 * message on ANY returned status (including the unroutable ones) and only
 * dead-letters when the resolver THROWS (an unexpected/transient error).
 */
export enum IngestInboundEmailStatus {
  /** Reply target Activity is missing / deleted / in another workspace. */
  ActivityNotFound = 'ACTIVITY_NOT_FOUND',
  /** Reply appended to an existing Activity. */
  Appended = 'APPENDED',
  /** New assetless Activity created (+ first message). */
  Created = 'CREATED',
  /** This Message-ID was already ingested — idempotent no-op. */
  Duplicate = 'DUPLICATE',
  /** No (non-deleted) workspace owns this slug — nothing created. */
  UnknownSlug = 'UNKNOWN_SLUG',
  /** Workspace exists but email is disabled — nothing created. */
  WorkspaceDisabled = 'WORKSPACE_DISABLED'
}

export enum IntegrationProvider {
  CaseIh = 'CASE_IH',
  Geotab = 'GEOTAB',
  JohnDeere = 'JOHN_DEERE',
  PublicApi = 'PUBLIC_API'
}

export enum IntegrationRunStatus {
  Failed = 'FAILED',
  Partial = 'PARTIAL',
  Success = 'SUCCESS'
}

export enum IntegrationSyncStatus {
  Idle = 'IDLE',
  Running = 'RUNNING'
}

export enum IssueCreationSource {
  Anonymousweb = 'ANONYMOUSWEB',
  Email = 'EMAIL',
  Geotab = 'GEOTAB',
  JohnDeere = 'JOHN_DEERE',
  Phone = 'PHONE',
  Scanner = 'SCANNER'
}

export enum IssueMessageType {
  AccessRequestMessage = 'ACCESS_REQUEST_MESSAGE',
  AccessRequestResolved = 'ACCESS_REQUEST_RESOLVED',
  ReminderMessage = 'REMINDER_MESSAGE',
  RoutineExceptionMessage = 'ROUTINE_EXCEPTION_MESSAGE',
  RoutineMessage = 'ROUTINE_MESSAGE',
  RoutinePromptMessage = 'ROUTINE_PROMPT_MESSAGE',
  SystemMessage = 'SYSTEM_MESSAGE',
  SystemMessageReminderAlert = 'SYSTEM_MESSAGE_REMINDER_ALERT',
  UserMessage = 'USER_MESSAGE'
}

export enum IssueType {
  AccessRequest = 'ACCESS_REQUEST',
  Alert = 'ALERT',
  AllGood = 'ALL_GOOD',
  Campaign = 'CAMPAIGN',
  Feedback = 'FEEDBACK',
  Note = 'NOTE',
  Reminder = 'REMINDER',
  Report = 'REPORT',
  Routine = 'ROUTINE'
}

export enum MediaStatus {
  Pending = 'PENDING',
  Processed = 'PROCESSED'
}

export enum MediaType {
  Audio = 'AUDIO',
  Document = 'DOCUMENT',
  Image = 'IMAGE',
  Link = 'LINK',
  Video = 'VIDEO'
}

export enum RingInboundCallStatus {
  /** This providerCallId was already ingested — idempotent no-op, returns the existing Activity. */
  Duplicate = 'DUPLICATE',
  /** A new Activity was created (or an existing one re-rung) and the workspace's admins are ringing. */
  Ringing = 'RINGING',
  /** No (non-deleted) workspace owns the dialed number — nothing created. */
  UnknownNumber = 'UNKNOWN_NUMBER'
}

export enum RoleType {
  Account = 'ACCOUNT',
  System = 'SYSTEM'
}

export enum RoutineIdpStatus {
  Failed = 'FAILED',
  Pending = 'PENDING',
  Processed = 'PROCESSED',
  Processing = 'PROCESSING'
}

export enum RoutineType {
  Counter = 'COUNTER',
  QuickConfirm = 'QUICK_CONFIRM',
  Status = 'STATUS',
  Workflow = 'WORKFLOW'
}

export enum ScheduleDays {
  EveryDay = 'EVERY_DAY',
  WeekDays = 'WEEK_DAYS'
}

export enum Severity {
  Blue = 'blue',
  Green = 'green',
  Purple = 'purple',
  Red = 'red',
  Yellow = 'yellow'
}

export enum ShareSubjectType {
  Asset = 'ASSET',
  Issue = 'ISSUE',
  SideChat = 'SIDE_CHAT',
  Workspace = 'WORKSPACE'
}

export enum SmsRelayOutcome {
  Failed = 'FAILED',
  OptedOut = 'OPTED_OUT',
  Sent = 'SENT'
}

export enum SortDirectionEnum {
  Asc = 'ASC',
  Desc = 'DESC'
}

export enum TagBatchStatus {
  Delivered = 'DELIVERED',
  Ordered = 'ORDERED',
  Printer = 'PRINTER',
  Provisioned = 'PROVISIONED',
  Shipping = 'SHIPPING'
}

export enum TagStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE'
}

export enum TriggerType {
  Issue = 'ISSUE',
  Routine = 'ROUTINE'
}

export enum WorkspaceType {
  Oem = 'OEM',
  Producer = 'PRODUCER'
}
